import { defineMessages, IntlShape } from "react-intl";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import { Company, Person } from "@mapmycustomers/shared/types/entity";
import { AreaFilterCondition } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";

import localSettings from "@app/config/LocalSettings";
import { METERS_IN_MILE } from "@app/util/consts";
import { isEntityValue } from "@app/util/filters/Location/assert";

const messages = defineMessages({
  entityImperial: {
    id: "filters.location.previewMessage.entity.imperial",
    defaultMessage:
      "{miles} {miles, plural, one {mile} other {miles}} from {entityName, select, false {{entityType, select, accounts {the Company} contacts {the Person} other {the Record}}} other {{entityName}}}",
    description:
      "Human readable location filter preview when using imperial units and filtering around some record",
  },
  entitySi: {
    id: "filters.location.previewMessage.entity.si",
    defaultMessage:
      "{km}km from {entityName, select, false {{entityType, select, accounts {the Company} contacts {the Person} other {the Record}}} other {{entityName}}}",
    description:
      "Human readable location filter preview when using SI units and filtering around some record",
  },
  imperial: {
    id: "filters.location.previewMessage.imperial",
    defaultMessage:
      "{miles} {miles, plural, one {mile} other {miles}} from {hasLocation, select, true {{location}} other {({latitude}, {longitude})}}",
    description: "Human readable location filter preview when using imperial units",
  },
  si: {
    id: "filters.location.previewMessage.si",
    defaultMessage:
      "{km}km from {hasLocation, select, true {{location}} other {({latitude}, {longitude})}}",
    description: "Human readable location filter preview when using SI units",
  },
});

const getLocationPreview = (
  intl: IntlShape | undefined,
  value: AreaFilterCondition["value"],
  entity?: Company | Person
): string => {
  const useSiUnits = localSettings.doesUseSiUnits();
  if (!intl) {
    if (isEntityValue(value)) {
      const name =
        (entity?.name ?? value.entity.type === EntityType.COMPANY)
          ? "the Company"
          : value.entity.type === EntityType.PERSON
            ? "the Person"
            : "the Record";
      return useSiUnits
        ? `${value.distanceInMeters / 1000}km from ${name}`
        : `${value.distanceInMeters / METERS_IN_MILE} miles from ${name}`;
    } else {
      return useSiUnits
        ? `${value.distanceInMeters / 1000}km from ${
            value.location ?? `(${value.latitude}, ${value.longitude})`
          }`
        : `${value.distanceInMeters / METERS_IN_MILE} miles from ${
            value.location ?? `(${value.latitude}, ${value.longitude})`
          }`;
    }
  }

  if (isEntityValue(value)) {
    return intl.formatMessage(useSiUnits ? messages.entitySi : messages.entityImperial, {
      entityName: entity?.name ?? false,
      entityType: value.entity.type,
      km: value.distanceInMeters / 1000,
      miles: value.distanceInMeters / METERS_IN_MILE,
    });
  } else {
    let latitude;
    let longitude;

    if (!value.location) {
      // better format for numbers if we're gonna display then (and we're gonna since condition has no location string)
      latitude = intl.formatNumber(value.latitude, {
        maximumFractionDigits: 6,
        minimumFractionDigits: 1,
      });
      longitude = intl.formatNumber(value.longitude, {
        maximumFractionDigits: 6,
        minimumFractionDigits: 1,
      });
    }

    return intl.formatMessage(useSiUnits ? messages.si : messages.imperial, {
      hasLocation: !!value.location,
      km: value.distanceInMeters / 1000,
      latitude,
      location: value.location,
      longitude,
      miles: value.distanceInMeters / METERS_IN_MILE,
    });
  }
};

export default getLocationPreview;
